import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../layout/MainLayout";
import { hasPermission } from "../utils/permissions";
import { API_BASE } from "../config/orbisConfig";



function authHeaders(extra = {}) {
    const token = localStorage.getItem("cani_auth") || "";
    return {
        ...extra,
        Authorization: `Bearer ${token}`,
        "X-CANI-AUTH": token,
    };
}

const ENTITY_TYPES = [
    "gobierno",
    "empresa",
    "medio",
    "reportero",
    "parque_industrial",
    "comercio",
    "organismo",
    "universidad",
    "camara",
    "aliado",
    "contacto_personal",
    "otro",
];

const PRIORITY_OPTIONS = ["alta", "media", "baja"];
const STATUS_OPTIONS = ["activo", "seguimiento", "pausado", "archivado"];

const ACTIVITY_TYPE_OPTIONS = [
    "llamada",
    "correo",
    "whatsapp",
    "reunion",
    "evento",
    "visita",
    "invitacion",
    "seguimiento",
    "otro",
];

const ACTIVITY_CHANNEL_OPTIONS = [
    "telefono",
    "email",
    "whatsapp",
    "presencial",
    "zoom",
    "teams",
    "otro",
];

const ACTIVITY_STATUS_OPTIONS = [
    "programada",
    "realizada",
    "cancelada",
    "seguimiento",
];

const EMPTY_FORM = {
    name: "",
    entity_type: "empresa",
    subcategory: "",
    sector_text: "",
    region: "",
    city: "",
    address: "",
    phone: "",
    whatsapp: "",
    email: "",
    website: "",
    source: "",
    priority_level: "media",
    status: "activo",
    notes: "",
};

const EMPTY_ACTIVITY_FORM = {
    contact_id: "",
    type: "llamada",
    channel: "telefono",
    title: "",
    objective: "",
    result: "",
    status: "programada",
    start_datetime: "",
    end_datetime: "",
    next_action: "",
    next_action_date: "",
    notes: "",
};

function getContactDisplayName(contact) {
    return (
        contact?.full_name ||
        [contact?.first_name, contact?.last_name].filter(Boolean).join(" ").trim() ||
        "Sin nombre"
    );
}

function toDatetimeLocal(value) {
    if (!value) return "";
    return String(value).slice(0, 16).replace(" ", "T");
}

function fromDatetimeLocal(value) {
    if (!value) return null;
    return `${value.replace("T", " ")}:00`;
}

function Modal({ open, title, subtitle, onClose, children }) {
    if (!open) return null;

    return (
        <>
            <div
                className="fixed inset-0 bg-slate-950/45 z-[90]"
                onClick={onClose}
            />
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                <div className="w-full max-w-6xl max-h-[92vh] overflow-auto bg-white border border-cani-border rounded-2xl shadow-2xl">
                    <div className="sticky top-0 bg-white border-b border-cani-border px-4 py-3 flex items-center justify-between z-10">
                        <div>
                            <h3 className="text-sm font-semibold text-cani-dark">{title}</h3>
                            {subtitle ? (
                                <p className="text-[11px] text-gray-500">{subtitle}</p>
                            ) : null}
                        </div>

                        <button
                            type="button"
                            onClick={onClose}
                            className="px-3 py-1.5 rounded-lg border border-cani-border text-xs text-gray-700 hover:bg-cani-bg"
                        >
                            Cerrar
                        </button>
                    </div>

                    <div className="p-4">{children}</div>
                </div>
            </div>
        </>
    );
}

export default function DirectoryPage() {
    const canCreate = hasPermission("directory.create") || hasPermission("directory.view");
    const canEdit = hasPermission("directory.edit") || hasPermission("directory.view");
    const canDelete = hasPermission("directory.delete") || hasPermission("directory.view");
    const canImport = hasPermission("directory.create") || hasPermission("directory.view");
    const canExport = hasPermission("directory.view") || hasPermission("directory.create");

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState("");
    const [toast, setToast] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [formMode, setFormMode] = useState("create");
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importing, setImporting] = useState(false);

    const [filters, setFilters] = useState({
        search: "",
        entity_type: "",
        region: "",
        city: "",
        priority_level: "",
        status: "",
    });

    const [masterContacts, setMasterContacts] = useState([]);
    const [masterContactsLoading, setMasterContactsLoading] = useState(false);
    const [entityContacts, setEntityContacts] = useState([]);
    const [entityContactsLoading, setEntityContactsLoading] = useState(false);
    const [contactSearch, setContactSearch] = useState("");
    const [contactPickerId, setContactPickerId] = useState("");
    const [contactRoleLabel, setContactRoleLabel] = useState("");
    const [contactIsPrimary, setContactIsPrimary] = useState(false);
    const [linkingContact, setLinkingContact] = useState(false);

    const [activities, setActivities] = useState([]);
    const [activitiesLoading, setActivitiesLoading] = useState(false);
    const [activityForm, setActivityForm] = useState(EMPTY_ACTIVITY_FORM);
    const [activityEditId, setActivityEditId] = useState(null);
    const [savingActivity, setSavingActivity] = useState(false);

    useEffect(() => {
        fetchData();
        loadMasterContacts();
    }, []);

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 7000);
        return () => clearTimeout(t);
    }, [toast]);

    async function safeJson(url, options = {}) {
        const res = await fetch(url, options);
        const txt = await res.text();
        let data = {};
        try {
            data = txt ? JSON.parse(txt) : {};
        } catch {
            throw new Error(`Respuesta no JSON: ${txt.slice(0, 250)}`);
        }

        if (!res.ok || data.success === false) {
            throw new Error(data.detail || data.error || `HTTP ${res.status}`);
        }

        return data;
    }

    async function fetchData() {
        try {
            setLoading(true);
            setError("");

            const data = await safeJson(`${API_BASE}/directory_entities_list.php`, {
                headers: authHeaders(),
            });

            setRows(Array.isArray(data) ? data : data.rows || []);
        } catch (err) {
            console.error(err);
            setError(err.message || "No se pudo cargar el directorio.");
        } finally {
            setLoading(false);
        }
    }

    async function loadMasterContacts() {
        try {
            setMasterContactsLoading(true);
            const data = await safeJson(`${API_BASE}/contacts_list.php`, {
                headers: authHeaders(),
            });
            setMasterContacts(Array.isArray(data) ? data : data.rows || []);
        } catch (err) {
            console.error(err);
            setMasterContacts([]);
            setToast({ type: "error", msg: err.message || "No se pudo cargar el catálogo de contactos." });
        } finally {
            setMasterContactsLoading(false);
        }
    }

    async function loadEntityContacts(entityId) {
        if (!entityId) {
            setEntityContacts([]);
            return;
        }

        try {
            setEntityContactsLoading(true);
            const data = await safeJson(`${API_BASE}/directory_entity_contacts_list.php?entity_id=${entityId}`, {
                headers: authHeaders(),
            });
            setEntityContacts(Array.isArray(data) ? data : data.items || []);
        } catch (err) {
            console.error(err);
            setEntityContacts([]);
            setToast({ type: "error", msg: err.message || "No se pudieron cargar los contactos vinculados." });
        } finally {
            setEntityContactsLoading(false);
        }
    }

    async function loadActivities(entityId) {
        if (!entityId) {
            setActivities([]);
            return;
        }

        try {
            setActivitiesLoading(true);
            const data = await safeJson(`${API_BASE}/directory_activities_list.php?entity_id=${entityId}`, {
                headers: authHeaders(),
            });
            setActivities(Array.isArray(data) ? data : data.items || []);
        } catch (err) {
            console.error(err);
            setActivities([]);
            setToast({ type: "error", msg: err.message || "No se pudieron cargar las actividades." });
        } finally {
            setActivitiesLoading(false);
        }
    }

    function resetActivityForm() {
        setActivityEditId(null);
        setActivityForm(EMPTY_ACTIVITY_FORM);
    }

    function openNew() {
        setFormMode("create");
        setEditingId(null);
        setForm(EMPTY_FORM);
        setEntityContacts([]);
        setActivities([]);
        setContactSearch("");
        setContactPickerId("");
        setContactRoleLabel("");
        setContactIsPrimary(false);
        resetActivityForm();
        setShowModal(true);
    }

    async function openEdit(row) {
        setFormMode("edit");
        setEditingId(row.id);
        setForm({
            name: row.name || "",
            entity_type: row.entity_type || "empresa",
            subcategory: row.subcategory || "",
            sector_text: row.sector_text || "",
            region: row.region || "",
            city: row.city || "",
            address: row.address || "",
            phone: row.phone || "",
            whatsapp: row.whatsapp || "",
            email: row.email || "",
            website: row.website || "",
            source: row.source || "",
            priority_level: row.priority_level || "media",
            status: row.status || "activo",
            notes: row.notes || "",
        });

        setContactSearch("");
        setContactPickerId("");
        setContactRoleLabel("");
        setContactIsPrimary(false);
        resetActivityForm();
        setShowModal(true);

        await Promise.all([
            loadEntityContacts(row.id),
            loadActivities(row.id),
        ]);
    }

    function closeModal() {
        setShowModal(false);
        setEditingId(null);
        setFormMode("create");
        setForm(EMPTY_FORM);
        setEntityContacts([]);
        setActivities([]);
        setContactSearch("");
        setContactPickerId("");
        setContactRoleLabel("");
        setContactIsPrimary(false);
        resetActivityForm();
    }

    function closeImportModal() {
        setShowImportModal(false);
        setImportFile(null);
        const input = document.getElementById("directory-import-file");
        if (input) input.value = "";
    }

    function handleChange(e) {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    }

    function handleActivityChange(e) {
        const { name, value } = e.target;
        setActivityForm((prev) => ({ ...prev, [name]: value }));
    }

    async function handleSubmit(e) {
        e.preventDefault();

        if (!form.name.trim()) {
            setError("El nombre es obligatorio.");
            return;
        }

        try {
            setSaving(true);
            setError("");

            const payload = {
                ...form,
                id: formMode === "edit" ? editingId : undefined,
            };

            const endpoint =
                formMode === "edit"
                    ? `${API_BASE}/directory_entity_update.php`
                    : `${API_BASE}/directory_entity_create.php`;

            await safeJson(endpoint, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(payload),
            });

            setToast({
                type: "ok",
                msg:
                    formMode === "edit"
                        ? "Registro actualizado correctamente."
                        : "Registro creado correctamente.",
            });

            closeModal();
            await fetchData();
        } catch (err) {
            console.error(err);
            setError(err.message || "No se pudo guardar el registro.");
        } finally {
            setSaving(false);
        }
    }

    async function handleDelete() {
        if (!editingId) return;
        if (!window.confirm("¿Eliminar este registro del directorio?")) return;

        try {
            setDeleting(true);
            setError("");

            await safeJson(`${API_BASE}/directory_entity_delete.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({ id: editingId }),
            });

            setToast({ type: "ok", msg: "Registro eliminado correctamente." });
            closeModal();
            await fetchData();
        } catch (err) {
            console.error(err);
            setError(err.message || "No se pudo eliminar el registro.");
        } finally {
            setDeleting(false);
        }
    }

    async function handleAttachContact() {
        if (!editingId) {
            setToast({ type: "error", msg: "Guarda primero el registro del directorio." });
            return;
        }

        if (!contactPickerId) {
            setToast({ type: "error", msg: "Selecciona un contacto del catálogo maestro." });
            return;
        }

        try {
            setLinkingContact(true);

            await safeJson(`${API_BASE}/directory_entity_contact_attach.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    entity_id: editingId,
                    contact_id: Number(contactPickerId),
                    role_label: contactRoleLabel || null,
                    is_primary: contactIsPrimary ? 1 : 0,
                }),
            });

            setToast({ type: "ok", msg: "Contacto vinculado correctamente." });
            setContactPickerId("");
            setContactRoleLabel("");
            setContactIsPrimary(false);
            await loadEntityContacts(editingId);
        } catch (err) {
            console.error(err);
            setToast({ type: "error", msg: err.message || "No se pudo vincular el contacto." });
        } finally {
            setLinkingContact(false);
        }
    }

    async function handleDetachContact(linkId) {
        if (!window.confirm("¿Quitar este contacto del registro?")) return;

        try {
            setLinkingContact(true);

            await safeJson(`${API_BASE}/directory_entity_contact_detach.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({ id: linkId }),
            });

            setToast({ type: "ok", msg: "Contacto desvinculado correctamente." });
            await loadEntityContacts(editingId);
        } catch (err) {
            console.error(err);
            setToast({ type: "error", msg: err.message || "No se pudo desvincular el contacto." });
        } finally {
            setLinkingContact(false);
        }
    }

    function startEditActivity(act) {
        setActivityEditId(act.id);
        setActivityForm({
            contact_id: act.contact_id ? String(act.contact_id) : "",
            type: act.type || "llamada",
            channel: act.channel || "telefono",
            title: act.title || "",
            objective: act.objective || "",
            result: act.result || "",
            status: act.status || "programada",
            start_datetime: toDatetimeLocal(act.start_datetime),
            end_datetime: toDatetimeLocal(act.end_datetime),
            next_action: act.next_action || "",
            next_action_date: toDatetimeLocal(act.next_action_date),
            notes: act.notes || "",
        });
    }

    async function handleSaveActivity(e) {
        e.preventDefault();

        if (!editingId) {
            setToast({ type: "error", msg: "Guarda primero el registro del directorio." });
            return;
        }

        if (!activityForm.title.trim()) {
            setToast({ type: "error", msg: "El título de la actividad es obligatorio." });
            return;
        }

        try {
            setSavingActivity(true);

            const payload = {
                id: activityEditId || undefined,
                entity_id: editingId,
                contact_id: activityForm.contact_id ? Number(activityForm.contact_id) : null,
                type: activityForm.type,
                channel: activityForm.channel || null,
                title: activityForm.title.trim(),
                objective: activityForm.objective || null,
                result: activityForm.result || null,
                status: activityForm.status || null,
                start_datetime: fromDatetimeLocal(activityForm.start_datetime),
                end_datetime: fromDatetimeLocal(activityForm.end_datetime),
                next_action: activityForm.next_action || null,
                next_action_date: fromDatetimeLocal(activityForm.next_action_date),
                notes: activityForm.notes || null,
            };

            const endpoint = activityEditId
                ? `${API_BASE}/directory_activity_update.php`
                : `${API_BASE}/directory_activity_create.php`;

            await safeJson(endpoint, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(payload),
            });

            setToast({
                type: "ok",
                msg: activityEditId ? "Actividad actualizada." : "Actividad creada.",
            });

            resetActivityForm();
            await loadActivities(editingId);
        } catch (err) {
            console.error(err);
            setToast({ type: "error", msg: err.message || "No se pudo guardar la actividad." });
        } finally {
            setSavingActivity(false);
        }
    }

    async function handleDeleteActivity(id) {
        if (!window.confirm("¿Eliminar esta actividad?")) return;

        try {
            setSavingActivity(true);

            await safeJson(`${API_BASE}/directory_activity_delete.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({ id }),
            });

            setToast({ type: "ok", msg: "Actividad eliminada." });

            if (activityEditId === id) {
                resetActivityForm();
            }

            await loadActivities(editingId);
        } catch (err) {
            console.error(err);
            setToast({ type: "error", msg: err.message || "No se pudo eliminar la actividad." });
        } finally {
            setSavingActivity(false);
        }
    }

    async function handleImportSubmit(e) {
        e.preventDefault();

        if (!importFile) {
            setToast({ type: "error", msg: "Selecciona un archivo Excel para importar." });
            return;
        }

        try {
            setImporting(true);

            const fd = new FormData();
            fd.append("file", importFile);

            const res = await fetch(`${API_BASE}/directory_entities_import.php`, {
                method: "POST",
                headers: authHeaders(),
                body: fd,
            });

            const txt = await res.text();
            let data = {};
            try {
                data = txt ? JSON.parse(txt) : {};
            } catch {
                throw new Error(`Respuesta no JSON: ${txt.slice(0, 400)}`);
            }

            if (!res.ok || data.success === false) {
                throw new Error(
                    data.detail
                        ? `${data.error || "Error"} ${data.detail}`
                        : (data.error || "No se pudo importar el archivo.")
                );
            }

            const sheetSummary = Array.isArray(data.sheets)
                ? data.sheets
                    .map(
                        (s) =>
                            `${s.sheet}: procesadas ${s.processed || 0}, insertadas ${s.inserted || 0}, omitidas ${s.skipped || 0}`
                    )
                    .join(" | ")
                : "";

            setToast({
                type: "ok",
                msg:
                    `Importación completada. Procesadas: ${data.processed || 0}. ` +
                    `Insertadas: ${data.inserted || 0}. ` +
                    `Omitidas: ${data.skipped || 0}.` +
                    (sheetSummary ? ` Detalle por hoja: ${sheetSummary}` : ""),
            });

            closeImportModal();
            await fetchData();
        } catch (err) {
            console.error(err);
            setToast({ type: "error", msg: err.message || "Error al importar directorio." });
        } finally {
            setImporting(false);
        }
    }

    function handleDownloadTemplate() {
        const token = localStorage.getItem("cani_auth") || "";
        const url = `${API_BASE}/directory_entities_template.php?token=${encodeURIComponent(token)}`;
        window.open(url, "_blank");
    }

    function handleExportExcel() {
        const token = localStorage.getItem("cani_auth") || "";
        const url = `${API_BASE}/directory_entities_export.php?token=${encodeURIComponent(token)}`;
        window.open(url, "_blank");
    }

    const stats = useMemo(() => {
        return {
            total: rows.length,
            gobierno: rows.filter((r) => r.entity_type === "gobierno").length,
            medios: rows.filter((r) => r.entity_type === "medio" || r.entity_type === "reportero").length,
            empresas: rows.filter((r) => r.entity_type === "empresa").length,
            activos: rows.filter((r) => r.status === "activo").length,
            seguimiento: rows.filter((r) => r.status === "seguimiento").length,
        };
    }, [rows]);

    const filteredRows = useMemo(() => {
        const q = filters.search.trim().toLowerCase();

        return rows.filter((r) => {
            const matchesSearch =
                !q ||
                [
                    r.name,
                    r.entity_type,
                    r.subcategory,
                    r.sector_text,
                    r.region,
                    r.city,
                    r.phone,
                    r.whatsapp,
                    r.email,
                    r.website,
                    r.source,
                    r.notes,
                ]
                    .filter(Boolean)
                    .some((v) => String(v).toLowerCase().includes(q));

            const matchesType = !filters.entity_type || r.entity_type === filters.entity_type;
            const matchesRegion = !filters.region || (r.region || "") === filters.region;
            const matchesCity =
                !filters.city ||
                (r.city || "").toLowerCase().includes(filters.city.trim().toLowerCase());
            const matchesPriority =
                !filters.priority_level || r.priority_level === filters.priority_level;
            const matchesStatus = !filters.status || r.status === filters.status;

            return (
                matchesSearch &&
                matchesType &&
                matchesRegion &&
                matchesCity &&
                matchesPriority &&
                matchesStatus
            );
        });
    }, [rows, filters]);

    const regions = useMemo(() => {
        const set = new Set(rows.map((r) => r.region).filter(Boolean));
        return Array.from(set).sort();
    }, [rows]);

    const filteredMasterContacts = useMemo(() => {
        const q = contactSearch.trim().toLowerCase();
        const linkedIds = new Set(entityContacts.map((c) => String(c.contact_id || c.id)));

        return masterContacts.filter((c) => {
            const id = String(c.id);
            if (linkedIds.has(id)) return false;

            if (!q) return true;

            return [
                getContactDisplayName(c),
                c.email,
                c.mobile,
                c.phone,
                c.position,
                c.account_name,
            ]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q));
        });
    }, [masterContacts, entityContacts, contactSearch]);

    return (
        <MainLayout>
            <div className="p-4 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                        <h2 className="text-lg md:text-xl font-semibold text-cani-dark">
                            Directorio estratégico
                        </h2>
                        <p className="text-xs md:text-sm text-gray-500">
                            Ecosistema de relaciones, networking, medios, gobierno, aliados y entidades clave.
                        </p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={fetchData}
                            className="px-3 py-2 rounded-lg border border-cani-border text-gray-700 text-xs hover:bg-cani-bg"
                        >
                            Recargar
                        </button>

                        {canExport ? (
                            <button
                                type="button"
                                onClick={handleExportExcel}
                                className="px-3 py-2 rounded-lg border border-cani-border text-gray-700 text-xs hover:bg-cani-bg"
                            >
                                Exportar Excel
                            </button>
                        ) : null}

                        {canImport ? (
                            <button
                                type="button"
                                onClick={() => setShowImportModal(true)}
                                className="px-3 py-2 rounded-lg border border-cani-border text-gray-700 text-xs hover:bg-cani-bg"
                            >
                                Importar Excel
                            </button>
                        ) : null}

                        {canCreate ? (
                            <button
                                type="button"
                                onClick={openNew}
                                className="px-3 py-2 rounded-lg bg-cani-primary text-white text-xs hover:bg-cani-primaryLight"
                            >
                                + Nuevo registro
                            </button>
                        ) : null}
                    </div>
                </div>

                {error ? (
                    <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        {error}
                    </div>
                ) : null}

                <div className="grid grid-cols-2 xl:grid-cols-6 gap-3">
                    <div className="bg-white border border-cani-border rounded-xl px-4 py-3 shadow-sm">
                        <div className="text-[11px] text-gray-500">Total</div>
                        <div className="text-xl font-semibold text-cani-dark">{stats.total}</div>
                    </div>
                    <div className="bg-white border border-cani-border rounded-xl px-4 py-3 shadow-sm">
                        <div className="text-[11px] text-gray-500">Gobierno</div>
                        <div className="text-xl font-semibold text-cani-dark">{stats.gobierno}</div>
                    </div>
                    <div className="bg-white border border-cani-border rounded-xl px-4 py-3 shadow-sm">
                        <div className="text-[11px] text-gray-500">Medios</div>
                        <div className="text-xl font-semibold text-cani-dark">{stats.medios}</div>
                    </div>
                    <div className="bg-white border border-cani-border rounded-xl px-4 py-3 shadow-sm">
                        <div className="text-[11px] text-gray-500">Empresas</div>
                        <div className="text-xl font-semibold text-cani-dark">{stats.empresas}</div>
                    </div>
                    <div className="bg-white border border-cani-border rounded-xl px-4 py-3 shadow-sm">
                        <div className="text-[11px] text-gray-500">Activos</div>
                        <div className="text-xl font-semibold text-cani-dark">{stats.activos}</div>
                    </div>
                    <div className="bg-white border border-cani-border rounded-xl px-4 py-3 shadow-sm">
                        <div className="text-[11px] text-gray-500">Seguimiento</div>
                        <div className="text-xl font-semibold text-cani-dark">{stats.seguimiento}</div>
                    </div>
                </div>

                <div className="bg-white border border-cani-border rounded-xl shadow-sm p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
                        <div className="xl:col-span-2">
                            <label className="block text-[11px] text-gray-600 mb-1">Buscar</label>
                            <input
                                type="text"
                                value={filters.search}
                                onChange={(e) =>
                                    setFilters((prev) => ({ ...prev, search: e.target.value }))
                                }
                                placeholder="Nombre, tipo, ciudad, notas, contacto..."
                                className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-cani-primary"
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] text-gray-600 mb-1">Tipo</label>
                            <select
                                value={filters.entity_type}
                                onChange={(e) =>
                                    setFilters((prev) => ({ ...prev, entity_type: e.target.value }))
                                }
                                className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-cani-primary"
                            >
                                <option value="">Todos</option>
                                {ENTITY_TYPES.map((type) => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-[11px] text-gray-600 mb-1">Región</label>
                            <select
                                value={filters.region}
                                onChange={(e) =>
                                    setFilters((prev) => ({ ...prev, region: e.target.value }))
                                }
                                className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-cani-primary"
                            >
                                <option value="">Todas</option>
                                {regions.map((region) => (
                                    <option key={region} value={region}>
                                        {region}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-[11px] text-gray-600 mb-1">Ciudad</label>
                            <input
                                type="text"
                                value={filters.city}
                                onChange={(e) =>
                                    setFilters((prev) => ({ ...prev, city: e.target.value }))
                                }
                                placeholder="Filtrar ciudad..."
                                className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-cani-primary"
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] text-gray-600 mb-1">Prioridad</label>
                            <select
                                value={filters.priority_level}
                                onChange={(e) =>
                                    setFilters((prev) => ({ ...prev, priority_level: e.target.value }))
                                }
                                className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-cani-primary"
                            >
                                <option value="">Todas</option>
                                {PRIORITY_OPTIONS.map((p) => (
                                    <option key={p} value={p}>
                                        {p}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-[11px] text-gray-600 mb-1">Estatus</label>
                            <select
                                value={filters.status}
                                onChange={(e) =>
                                    setFilters((prev) => ({ ...prev, status: e.target.value }))
                                }
                                className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-cani-primary"
                            >
                                <option value="">Todos</option>
                                {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="bg-white border border-cani-border rounded-xl shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-cani-border flex items-center justify-between">
                        <h3 className="text-xs font-semibold text-cani-dark">
                            Registros del directorio
                        </h3>
                        <span className="text-[11px] text-gray-500">
                            Mostrando: {filteredRows.length}
                        </span>
                    </div>

                    <div className="max-h-[65vh] overflow-auto">
                        <table className="min-w-full text-xs">
                            <thead className="bg-cani-bg">
                                <tr className="text-left text-[11px] font-semibold text-gray-600">
                                    <th className="px-3 py-2">Entidad</th>
                                    <th className="px-3 py-2 hidden md:table-cell">Tipo</th>
                                    <th className="px-3 py-2 hidden lg:table-cell">Ubicación</th>
                                    <th className="px-3 py-2 hidden xl:table-cell">Contacto</th>
                                    <th className="px-3 py-2">Prioridad</th>
                                    <th className="px-3 py-2">Estatus</th>
                                    <th className="px-3 py-2 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} className="px-3 py-4 text-center text-gray-500">
                                            Cargando directorio...
                                        </td>
                                    </tr>
                                ) : null}

                                {!loading && filteredRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-3 py-4 text-center text-gray-400">
                                            No hay registros para mostrar.
                                        </td>
                                    </tr>
                                ) : null}

                                {!loading &&
                                    filteredRows.map((row) => (
                                        <tr
                                            key={row.id}
                                            className="border-t border-cani-border hover:bg-cani-bg/60"
                                        >
                                            <td className="px-3 py-2 align-top">
                                                <div className="font-medium text-cani-dark">{row.name}</div>
                                                <div className="text-[10px] text-gray-500 mt-1">
                                                    {row.subcategory || row.sector_text || "—"}
                                                </div>
                                            </td>

                                            <td className="px-3 py-2 hidden md:table-cell text-gray-600 align-top">
                                                {row.entity_type || "—"}
                                            </td>

                                            <td className="px-3 py-2 hidden lg:table-cell text-gray-600 align-top">
                                                <div>{row.region || "—"}</div>
                                                <div className="text-[10px] text-gray-500 mt-1">{row.city || "—"}</div>
                                            </td>

                                            <td className="px-3 py-2 hidden xl:table-cell text-gray-600 align-top">
                                                <div>{row.phone || row.whatsapp || "—"}</div>
                                                <div className="text-[10px] text-gray-500 mt-1">{row.email || "—"}</div>
                                            </td>

                                            <td className="px-3 py-2 align-top">
                                                <span
                                                    className={`inline-flex px-2 py-1 rounded-full border text-[10px] ${row.priority_level === "alta"
                                                            ? "bg-red-50 text-red-700 border-red-200"
                                                            : row.priority_level === "media"
                                                                ? "bg-amber-50 text-amber-700 border-amber-200"
                                                                : "bg-gray-50 text-gray-700 border-gray-200"
                                                        }`}
                                                >
                                                    {row.priority_level || "media"}
                                                </span>
                                            </td>

                                            <td className="px-3 py-2 align-top">
                                                <span
                                                    className={`inline-flex px-2 py-1 rounded-full border text-[10px] ${row.status === "activo"
                                                            ? "bg-green-50 text-green-700 border-green-200"
                                                            : row.status === "seguimiento"
                                                                ? "bg-blue-50 text-blue-700 border-blue-200"
                                                                : row.status === "pausado"
                                                                    ? "bg-amber-50 text-amber-700 border-amber-200"
                                                                    : "bg-gray-50 text-gray-700 border-gray-200"
                                                        }`}
                                                >
                                                    {row.status || "activo"}
                                                </span>
                                            </td>

                                            <td className="px-3 py-2 text-right align-top">
                                                {canEdit ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => openEdit(row)}
                                                        className="px-2 py-1 rounded-lg border border-cani-border hover:bg-cani-bg"
                                                    >
                                                        Editar
                                                    </button>
                                                ) : null}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <Modal
                    open={showImportModal}
                    title="Importar directorio estratégico"
                    subtitle="Carga masiva desde Excel"
                    onClose={closeImportModal}
                >
                    <form onSubmit={handleImportSubmit} className="space-y-4">
                        <div className="rounded-xl border border-cani-border bg-cani-bg/40 p-4">
                            <div className="text-[12px] font-semibold text-cani-dark mb-2">
                                Formato esperado del Excel
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-[12px] text-gray-600">
                                <div>A: Nombre entidad</div>
                                <div>B: Tipo entidad</div>
                                <div>C: Subcategoría</div>
                                <div>D: Sector / giro</div>
                                <div>E: Nombre contacto principal</div>
                                <div>F: Puesto / rol</div>
                                <div>G: Teléfono</div>
                                <div>H: WhatsApp</div>
                                <div>I: Email</div>
                                <div>J: Región</div>
                                <div>K: Ciudad</div>
                                <div>L: Dirección</div>
                                <div>M: Sitio web</div>
                                <div>N: Fuente / origen</div>
                                <div>O: Prioridad</div>
                                <div>P: Estatus</div>
                                <div>Q: Nota 1</div>
                                <div>R: Nota 2</div>
                                <div>S: Nota 3</div>
                            </div>
                        </div>

                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                            La fila 1 debe contener exactamente esos encabezados. Si el archivo trae nombres distintos, el sistema lo rechazará.
                        </div>

                        <div>
                            <label className="block text-[11px] text-gray-600 mb-1">
                                Archivo Excel
                            </label>
                            <input
                                id="directory-import-file"
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                                className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                            />
                            {importFile ? (
                                <div className="mt-2 text-[11px] text-gray-500">
                                    Archivo seleccionado:{" "}
                                    <span className="font-medium text-cani-dark">
                                        {importFile.name}
                                    </span>
                                </div>
                            ) : null}
                        </div>

                        <div className="flex justify-between gap-2 flex-wrap">
                            <button
                                type="button"
                                onClick={handleDownloadTemplate}
                                className="px-3 py-1.5 rounded-lg border border-cani-border text-gray-700 text-xs hover:bg-cani-bg"
                                disabled={importing}
                            >
                                Descargar plantilla
                            </button>

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={closeImportModal}
                                    className="px-3 py-1.5 rounded-lg border border-cani-border text-gray-700 text-xs hover:bg-cani-bg"
                                    disabled={importing}
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="submit"
                                    disabled={importing || !importFile}
                                    className="px-3 py-1.5 rounded-lg bg-cani-primary text-white text-xs hover:bg-cani-primaryLight disabled:opacity-60"
                                >
                                    {importing ? "Importando..." : "Importar"}
                                </button>
                            </div>
                        </div>
                    </form>
                </Modal>

                <Modal
                    open={showModal}
                    title={
                        formMode === "create"
                            ? "Nuevo registro del directorio"
                            : "Editar registro del directorio"
                    }
                    subtitle={
                        formMode === "create"
                            ? "Alta de entidad del ecosistema"
                            : editingId
                                ? `ID ${editingId}`
                                : ""
                    }
                    onClose={closeModal}
                >
                    <div className="space-y-5">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                <div className="md:col-span-2 xl:col-span-3">
                                    <label className="block text-[11px] text-gray-600 mb-1">Nombre *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-[11px] text-gray-600 mb-1">Tipo *</label>
                                    <select
                                        name="entity_type"
                                        value={form.entity_type}
                                        onChange={handleChange}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                    >
                                        {ENTITY_TYPES.map((type) => (
                                            <option key={type} value={type}>
                                                {type}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-[11px] text-gray-600 mb-1">Subcategoría</label>
                                    <input
                                        type="text"
                                        name="subcategory"
                                        value={form.subcategory}
                                        onChange={handleChange}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[11px] text-gray-600 mb-1">Sector / giro</label>
                                    <input
                                        type="text"
                                        name="sector_text"
                                        value={form.sector_text}
                                        onChange={handleChange}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[11px] text-gray-600 mb-1">Región</label>
                                    <input
                                        type="text"
                                        name="region"
                                        value={form.region}
                                        onChange={handleChange}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[11px] text-gray-600 mb-1">Ciudad</label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={form.city}
                                        onChange={handleChange}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                    />
                                </div>

                                <div className="md:col-span-2 xl:col-span-3">
                                    <label className="block text-[11px] text-gray-600 mb-1">Dirección</label>
                                    <input
                                        type="text"
                                        name="address"
                                        value={form.address}
                                        onChange={handleChange}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[11px] text-gray-600 mb-1">Teléfono</label>
                                    <input
                                        type="text"
                                        name="phone"
                                        value={form.phone}
                                        onChange={handleChange}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[11px] text-gray-600 mb-1">WhatsApp</label>
                                    <input
                                        type="text"
                                        name="whatsapp"
                                        value={form.whatsapp}
                                        onChange={handleChange}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[11px] text-gray-600 mb-1">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                    />
                                </div>

                                <div className="md:col-span-2 xl:col-span-3">
                                    <label className="block text-[11px] text-gray-600 mb-1">Sitio web</label>
                                    <input
                                        type="text"
                                        name="website"
                                        value={form.website}
                                        onChange={handleChange}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[11px] text-gray-600 mb-1">Fuente / origen</label>
                                    <input
                                        type="text"
                                        name="source"
                                        value={form.source}
                                        onChange={handleChange}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[11px] text-gray-600 mb-1">Prioridad</label>
                                    <select
                                        name="priority_level"
                                        value={form.priority_level}
                                        onChange={handleChange}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                    >
                                        {PRIORITY_OPTIONS.map((p) => (
                                            <option key={p} value={p}>
                                                {p}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-[11px] text-gray-600 mb-1">Estatus</label>
                                    <select
                                        name="status"
                                        value={form.status}
                                        onChange={handleChange}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                    >
                                        {STATUS_OPTIONS.map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="md:col-span-2 xl:col-span-3">
                                    <label className="block text-[11px] text-gray-600 mb-1">Notas</label>
                                    <textarea
                                        name="notes"
                                        value={form.notes}
                                        onChange={handleChange}
                                        rows={4}
                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs resize-none"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-between items-center gap-2 pt-2">
                                <div>
                                    {formMode === "edit" && canDelete ? (
                                        <button
                                            type="button"
                                            onClick={handleDelete}
                                            disabled={deleting}
                                            className="px-3 py-1.5 rounded-lg border border-red-200 text-red-700 text-xs hover:bg-red-50 disabled:opacity-60"
                                        >
                                            {deleting ? "Eliminando..." : "Eliminar"}
                                        </button>
                                    ) : (
                                        <span className="text-[11px] text-gray-400">—</span>
                                    )}
                                </div>

                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        className="px-3 py-1.5 rounded-lg border border-cani-border text-gray-700 text-xs hover:bg-cani-bg"
                                    >
                                        Cancelar
                                    </button>

                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="px-3 py-1.5 rounded-lg bg-cani-primary text-white text-xs hover:bg-cani-primaryLight disabled:opacity-60"
                                    >
                                        {saving
                                            ? "Guardando..."
                                            : formMode === "create"
                                                ? "Crear registro"
                                                : "Guardar cambios"}
                                    </button>
                                </div>
                            </div>
                        </form>

                        {editingId ? (
                            <div className="border-t border-cani-border pt-4 space-y-6">
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <h4 className="text-sm font-semibold text-cani-dark">
                                                Contactos vinculados
                                            </h4>
                                            <p className="text-[11px] text-gray-500">
                                                Relaciona personas del catálogo maestro con esta entidad.
                                            </p>
                                        </div>
                                        <span className="text-[11px] text-gray-500">
                                            {entityContactsLoading ? "Cargando..." : `${entityContacts.length} vínculo(s)`}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                        <div className="bg-white border border-cani-border rounded-xl p-4">
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-[11px] text-gray-600 mb-1">
                                                        Buscar en contactos
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={contactSearch}
                                                        onChange={(e) => setContactSearch(e.target.value)}
                                                        placeholder="Nombre, email, puesto, teléfono..."
                                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-[11px] text-gray-600 mb-1">
                                                        Seleccionar contacto
                                                    </label>
                                                    <select
                                                        value={contactPickerId}
                                                        onChange={(e) => setContactPickerId(e.target.value)}
                                                        disabled={masterContactsLoading || linkingContact}
                                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                                    >
                                                        <option value="">
                                                            {masterContactsLoading
                                                                ? "Cargando catálogo..."
                                                                : "— Seleccionar contacto —"}
                                                        </option>
                                                        {filteredMasterContacts.map((c) => (
                                                            <option key={c.id} value={String(c.id)}>
                                                                {getContactDisplayName(c)}
                                                                {c.position ? ` · ${c.position}` : ""}
                                                                {c.email ? ` · ${c.email}` : ""}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-[11px] text-gray-600 mb-1">
                                                        Rol en la entidad
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={contactRoleLabel}
                                                        onChange={(e) => setContactRoleLabel(e.target.value)}
                                                        placeholder="Ej. Titular, enlace, reportero, director..."
                                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                                    />
                                                </div>

                                                <label className="flex items-center gap-2 text-[11px] text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        checked={contactIsPrimary}
                                                        onChange={(e) => setContactIsPrimary(e.target.checked)}
                                                    />
                                                    Marcar como contacto principal
                                                </label>

                                                <div className="flex justify-end">
                                                    <button
                                                        type="button"
                                                        onClick={handleAttachContact}
                                                        disabled={!contactPickerId || linkingContact}
                                                        className="px-3 py-1.5 rounded-lg bg-cani-primary text-white text-xs hover:bg-cani-primaryLight disabled:opacity-60"
                                                    >
                                                        {linkingContact ? "Guardando..." : "Vincular contacto"}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white border border-cani-border rounded-xl overflow-hidden">
                                            <div className="px-4 py-3 border-b border-cani-border">
                                                <h5 className="text-xs font-semibold text-cani-dark">
                                                    Lista de contactos vinculados
                                                </h5>
                                            </div>

                                            <div className="max-h-[320px] overflow-auto">
                                                {entityContactsLoading ? (
                                                    <div className="px-4 py-4 text-center text-[11px] text-gray-500">
                                                        Cargando contactos...
                                                    </div>
                                                ) : entityContacts.length === 0 ? (
                                                    <div className="px-4 py-4 text-center text-[11px] text-gray-400">
                                                        Aún no hay contactos vinculados.
                                                    </div>
                                                ) : (
                                                    <ul className="divide-y divide-cani-border text-[11px]">
                                                        {entityContacts.map((c) => (
                                                            <li key={c.id || `${c.entity_id}-${c.contact_id}`} className="px-4 py-3">
                                                                <div className="flex items-start justify-between gap-2">
                                                                    <div className="min-w-0">
                                                                        <div className="font-semibold text-cani-dark flex items-center gap-2 flex-wrap">
                                                                            <span>{getContactDisplayName(c)}</span>
                                                                            {Number(c.is_primary) === 1 ? (
                                                                                <span className="inline-flex px-2 py-1 rounded-full border border-green-200 bg-green-50 text-green-700 text-[10px]">
                                                                                    Principal
                                                                                </span>
                                                                            ) : null}
                                                                        </div>

                                                                        <div className="text-gray-600 mt-1">
                                                                            {c.role_label || c.position || "Sin rol"}
                                                                        </div>

                                                                        <div className="text-[10px] text-gray-500 mt-1">
                                                                            {c.email || "Sin email"} · {c.mobile || c.phone || "Sin teléfono"}
                                                                        </div>
                                                                    </div>

                                                                    {canEdit ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDetachContact(c.id)}
                                                                            className="px-2 py-1 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 shrink-0"
                                                                            disabled={linkingContact}
                                                                        >
                                                                            Quitar
                                                                        </button>
                                                                    ) : null}
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4 border-t border-cani-border pt-4">
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <h4 className="text-sm font-semibold text-cani-dark">
                                                Actividades del directorio
                                            </h4>
                                            <p className="text-[11px] text-gray-500">
                                                Seguimiento de llamadas, correos, reuniones, invitaciones y eventos.
                                            </p>
                                        </div>
                                        <span className="text-[11px] text-gray-500">
                                            {activitiesLoading ? "Cargando..." : `${activities.length} actividad(es)`}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                        <div className="bg-white border border-cani-border rounded-xl p-4">
                                            <div className="font-semibold text-cani-dark text-xs mb-3">
                                                {activityEditId ? "Editar actividad" : "Nueva actividad"}
                                            </div>

                                            <form onSubmit={handleSaveActivity} className="space-y-3">
                                                <div>
                                                    <label className="block text-[11px] text-gray-600 mb-1">
                                                        Contacto relacionado
                                                    </label>
                                                    <select
                                                        name="contact_id"
                                                        value={activityForm.contact_id}
                                                        onChange={handleActivityChange}
                                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                                    >
                                                        <option value="">— Sin contacto específico —</option>
                                                        {entityContacts.map((c) => (
                                                            <option key={c.contact_id} value={String(c.contact_id)}>
                                                                {getContactDisplayName(c)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-[11px] text-gray-600 mb-1">Tipo</label>
                                                        <select
                                                            name="type"
                                                            value={activityForm.type}
                                                            onChange={handleActivityChange}
                                                            className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                                        >
                                                            {ACTIVITY_TYPE_OPTIONS.map((item) => (
                                                                <option key={item} value={item}>
                                                                    {item}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label className="block text-[11px] text-gray-600 mb-1">Canal</label>
                                                        <select
                                                            name="channel"
                                                            value={activityForm.channel}
                                                            onChange={handleActivityChange}
                                                            className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                                        >
                                                            {ACTIVITY_CHANNEL_OPTIONS.map((item) => (
                                                                <option key={item} value={item}>
                                                                    {item}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-[11px] text-gray-600 mb-1">Título *</label>
                                                    <input
                                                        type="text"
                                                        name="title"
                                                        value={activityForm.title}
                                                        onChange={handleActivityChange}
                                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                                        required
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-[11px] text-gray-600 mb-1">Objetivo</label>
                                                    <textarea
                                                        name="objective"
                                                        value={activityForm.objective}
                                                        onChange={handleActivityChange}
                                                        rows={2}
                                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs resize-none"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-[11px] text-gray-600 mb-1">Resultado</label>
                                                    <textarea
                                                        name="result"
                                                        value={activityForm.result}
                                                        onChange={handleActivityChange}
                                                        rows={2}
                                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs resize-none"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-[11px] text-gray-600 mb-1">Estatus</label>
                                                    <select
                                                        name="status"
                                                        value={activityForm.status}
                                                        onChange={handleActivityChange}
                                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                                    >
                                                        {ACTIVITY_STATUS_OPTIONS.map((item) => (
                                                            <option key={item} value={item}>
                                                                {item}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-[11px] text-gray-600 mb-1">Inicio</label>
                                                        <input
                                                            type="datetime-local"
                                                            name="start_datetime"
                                                            value={activityForm.start_datetime}
                                                            onChange={handleActivityChange}
                                                            className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-[11px] text-gray-600 mb-1">Fin</label>
                                                        <input
                                                            type="datetime-local"
                                                            name="end_datetime"
                                                            value={activityForm.end_datetime}
                                                            onChange={handleActivityChange}
                                                            className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-[11px] text-gray-600 mb-1">Próximo paso</label>
                                                    <input
                                                        type="text"
                                                        name="next_action"
                                                        value={activityForm.next_action}
                                                        onChange={handleActivityChange}
                                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-[11px] text-gray-600 mb-1">Fecha próximo paso</label>
                                                    <input
                                                        type="datetime-local"
                                                        name="next_action_date"
                                                        value={activityForm.next_action_date}
                                                        onChange={handleActivityChange}
                                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-[11px] text-gray-600 mb-1">Notas</label>
                                                    <textarea
                                                        name="notes"
                                                        value={activityForm.notes}
                                                        onChange={handleActivityChange}
                                                        rows={3}
                                                        className="w-full border border-cani-border rounded-lg px-3 py-2 text-xs resize-none"
                                                    />
                                                </div>

                                                <div className="flex justify-end gap-2">
                                                    {activityEditId ? (
                                                        <button
                                                            type="button"
                                                            onClick={resetActivityForm}
                                                            className="px-3 py-1.5 rounded-lg border border-cani-border text-gray-700 text-xs hover:bg-cani-bg"
                                                        >
                                                            Cancelar
                                                        </button>
                                                    ) : null}

                                                    <button
                                                        type="submit"
                                                        disabled={savingActivity}
                                                        className="px-3 py-1.5 rounded-lg bg-cani-primary text-white text-xs hover:bg-cani-primaryLight disabled:opacity-60"
                                                    >
                                                        {savingActivity
                                                            ? "Guardando..."
                                                            : activityEditId
                                                                ? "Actualizar actividad"
                                                                : "Crear actividad"}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>

                                        <div className="bg-white border border-cani-border rounded-xl overflow-hidden">
                                            <div className="px-4 py-3 border-b border-cani-border">
                                                <h5 className="text-xs font-semibold text-cani-dark">
                                                    Historial de actividades
                                                </h5>
                                            </div>

                                            <div className="max-h-[520px] overflow-auto">
                                                {activitiesLoading ? (
                                                    <div className="px-4 py-4 text-center text-[11px] text-gray-500">
                                                        Cargando actividades...
                                                    </div>
                                                ) : activities.length === 0 ? (
                                                    <div className="px-4 py-4 text-center text-[11px] text-gray-400">
                                                        Aún no hay actividades registradas.
                                                    </div>
                                                ) : (
                                                    <ul className="divide-y divide-cani-border text-[11px]">
                                                        {activities.map((act) => (
                                                            <li key={act.id} className="px-4 py-3">
                                                                <div className="flex items-start justify-between gap-3">
                                                                    <div className="min-w-0">
                                                                        <div className="font-semibold text-cani-dark">
                                                                            {act.title}
                                                                        </div>
                                                                        <div className="text-gray-600 mt-1">
                                                                            {act.type} · {act.channel || "sin canal"} · {act.status || "sin estatus"}
                                                                        </div>
                                                                        {act.contact_name ? (
                                                                            <div className="text-[10px] text-gray-500 mt-1">
                                                                                Contacto: {act.contact_name}
                                                                            </div>
                                                                        ) : null}
                                                                        {act.start_datetime ? (
                                                                            <div className="text-[10px] text-gray-500 mt-1">
                                                                                Inicio: {act.start_datetime}
                                                                            </div>
                                                                        ) : null}
                                                                        {act.next_action ? (
                                                                            <div className="text-[10px] text-gray-500 mt-1">
                                                                                Próximo paso: {act.next_action}
                                                                            </div>
                                                                        ) : null}
                                                                        {act.notes ? (
                                                                            <div className="text-[10px] text-gray-500 mt-1">
                                                                                {act.notes}
                                                                            </div>
                                                                        ) : null}
                                                                    </div>

                                                                    <div className="flex flex-col gap-2 shrink-0">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => startEditActivity(act)}
                                                                            className="px-2 py-1 rounded-lg border border-cani-border hover:bg-cani-bg"
                                                                        >
                                                                            Editar
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDeleteActivity(act.id)}
                                                                            className="px-2 py-1 rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
                                                                        >
                                                                            Eliminar
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </Modal>

                {toast ? (
                    <div
                        className={`rounded-xl border shadow-lg px-4 py-3 ${toast.type === "error"
                                ? "bg-red-50 border-red-200"
                                : "bg-white border-cani-border"
                            }`}
                        style={{
                            position: "fixed",
                            right: 16,
                            bottom: 16,
                            zIndex: 3000,
                            maxWidth: 560,
                        }}
                    >
                        <div
                            style={{ fontWeight: 700, marginBottom: 6 }}
                            className={toast.type === "error" ? "text-red-700" : "text-cani-dark"}
                        >
                            {toast.type === "error" ? "Error" : "OK"}
                        </div>
                        <div style={{ color: "#111827" }}>{toast.msg}</div>
                    </div>
                ) : null}
            </div>
        </MainLayout>
    );
}
